import { DomainAvailabilityStatus } from '../../typing';
import { parseDomain } from '../../utils';
import { PremiumMyDomainsApiClient } from '../ApiClients/ProtoServicesApiClients';
import { ConnectionType } from '@wix/ambassador-premium-domains-v1-connected-domain/types';
import { ConnectOrBindLogger } from '../Loggers/DataLogger';
import { ManagementTypeEnumManagementType } from '@wix/ambassador-domains-userdomain-v1-user-domain/types';
import { getPremiumEssentialsSafe } from '@wix/premium-fed-sdk/@premium-essentials';
import { Specs } from '../../constants/Specs';
import { AssignmentType } from '@wix/ambassador-premium-domains-v1-domain-assignment/types';

/**
 * Assigns (or reassigns) a domain to a site. Picks between the legacy
 * `bindDomain` API and the Nile domain-assignment API based on the
 * `DOMAIN_ASSIGNMENT_FED_MIGRATION` experiment.
 *
 * Legacy `bindDomain` is an upsert (create-or-update). Nile splits that into
 * `reassignDomain` (update) and `createDomainAssignment` (create), so we try
 * the reassign first and fall back to create on 404 — letting Nile decide
 * which path the domain is on.
 */
const assignDomainToSite = async _ref => {
  let {
    httpClient,
    domainName,
    siteId
  } = _ref;
  const {
    experiments
  } = getPremiumEssentialsSafe() ?? {};
  const useNile = experiments == null ? void 0 : experiments[Specs.DOMAIN_ASSIGNMENT_FED_MIGRATION];
  if (useNile) {
    const {
      upsertDomainAssignment
    } = (await import('../ApiClients/DomainsApiClient')).DomainsApiClient.queries({
      httpClient
    });

    // AssignmentType.REDIRECT will act as PRIMARY for new assignments, and as SECONDARY for existing ones, so it works for both create and reassign without us having to check which case we're in
    await upsertDomainAssignment({
      domainAssignment: {
        domain: domainName,
        siteInfo: {
          id: siteId,
          assignmentType: AssignmentType.REDIRECT
        }
      }
    });
    return;
  }
  const {
    bindDomain
  } = PremiumMyDomainsApiClient.queries({
    httpClient
  });
  await bindDomain({
    domainName,
    siteId
  });
};

/// call userDomains.get
/// if exists -> myDomains.bind
/// if exception:permission_denied -> exception (belongs to another user)
/// if exception:not_found -> domainSearch.checkDomainAvailability ->
/// if available -> exception (requires registration)
/// if taken -> create-connected-domain connectionType by requestParam (

// subdomain:
// call userDomains.get
// if exists -> myDomains.bind
/// if exception:permission_denied -> exception (belongs to another user)
/// if exception:not_found -> create-connected-domain connectionType: undefined

export const connectOrBindDomain = async _ref2 => {
  let {
    httpClient,
    request: {
      domain,
      msid,
      connectionType,
      connectedByEntri
    }
  } = _ref2;
  const parsedDomain = domain.asciiDomain ? domain : parseDomain(domain);
  const rootDomain = parsedDomain.asciiDomain;
  const fullAsciiDomain = parsedDomain.fullAsciiDomain;
  const isSubdomain = !!parsedDomain.subdomain;
  ConnectOrBindLogger.onConnectOrBindStart == null || ConnectOrBindLogger.onConnectOrBindStart({
    domainName: parsedDomain == null ? void 0 : parsedDomain.formattedInput,
    connectionType,
    msid
  });
  try {
    if (isSubdomain) {
      return connectOrBindSubdomain({
        httpClient,
        subdomain: fullAsciiDomain,
        rootDomain,
        msid,
        connectedByEntri
      });
    } else {
      return connectOrBindRootDomain({
        httpClient,
        rootDomain,
        msid,
        connectionType,
        connectedByEntri
      });
    }
  } catch (e) {
    ConnectOrBindLogger.onConnectOrBindError == null || ConnectOrBindLogger.onConnectOrBindError({
      e: e,
      domainName: parsedDomain == null ? void 0 : parsedDomain.formattedInput,
      connectionType,
      msid
    });
    throw e;
  }
};
async function connectOrBindRootDomain(_ref3) {
  var _connectedDomain$site;
  let {
    httpClient,
    rootDomain,
    msid,
    connectionType,
    connectedByEntri
  } = _ref3;
  const {
    createConnectedDomain,
    deleteConnectedDomain,
    checkDomainAvailability,
    forceDomainCheck,
    userDomain
  } = (await import('../ApiClients/DomainsApiClient')).DomainsApiClient.queries({
    httpClient
  });
  const {
    error,
    domain,
    site
  } = await userDomain({
    domain: rootDomain,
    withSiteData: true
  }, {
    cacheTime: 0
  });
  const isRootDomainInAccount = !error;
  const rootDomainBelongsToAnotherWixAccount = error === 'belongsToAnotherAccount';
  ConnectOrBindLogger.onConnectOrBindRootDomainStart == null || ConnectOrBindLogger.onConnectOrBindRootDomainStart({
    domainName: rootDomain,
    connectionType,
    msid
  });
  ConnectOrBindLogger.onConnectOrBindOwnerCheck == null || ConnectOrBindLogger.onConnectOrBindOwnerCheck({
    domainName: rootDomain,
    connectionType,
    msid,
    isRootDomainInAccount,
    connectedDomain: domain
  });
  if (isRootDomainInAccount) {
    const isAlreadyConnectedToSite = (site == null ? void 0 : site.siteId) === msid;
    const siteId = msid ?? (site == null ? void 0 : site.siteId);
    const managementType = connectionType === 'dns' ? ManagementTypeEnumManagementType.DNS : ManagementTypeEnumManagementType.POINTING;
    if ((domain == null ? void 0 : domain.managementType) !== managementType && connectionType) {
      ConnectOrBindLogger.onConnectOrBindUpdateStart == null || ConnectOrBindLogger.onConnectOrBindUpdateStart({
        domainName: rootDomain,
        connectionType,
        msid
      });
      await deleteConnectedDomain({
        connectedDomainId: rootDomain
      });
      await createConnectedDomain({
        connectedByEntri: !!connectedByEntri,
        connectedDomain: {
          domain: rootDomain,
          connectionType: connectionType === 'pointing' ? ConnectionType.POINTING : ConnectionType.NAMESERVERS
        }
      });
      if (siteId) {
        await assignDomainToSite({
          httpClient,
          domainName: rootDomain,
          siteId
        });
      }
    }
    if (!isAlreadyConnectedToSite && (site == null ? void 0 : site.siteId) !== msid && msid) {
      await assignDomainToSite({
        httpClient,
        domainName: rootDomain,
        siteId: msid
      });
    }
    if ((domain == null ? void 0 : domain.managementType) === managementType) {
      await forceDomainCheck({
        domainName: rootDomain
      });
    }
    return {
      domainName: rootDomain,
      msid: msid ?? (site == null ? void 0 : site.siteId),
      prevSiteId: site == null ? void 0 : site.siteId
    };
  }
  if (rootDomainBelongsToAnotherWixAccount) {
    return {
      error: DomainAvailabilityStatus.DOMAIN_IN_ANOTHER_WIX_ACCOUNT
    };
  }
  try {
    var _availability$availab;
    const availability = await checkDomainAvailability({
      domain: rootDomain
    });
    const isRootDomainAvailable = (_availability$availab = availability.availability) == null ? void 0 : _availability$availab.available;
    if (isRootDomainAvailable) {
      return {
        error: DomainAvailabilityStatus.AVAILABLE_FOR_PURCHASE
      };
    }
  } catch (e) {
    var _e$response;
    // Don't delete this part it's for com.tr, gets 400 error on availabilty check
    const statusCode = (e == null || (_e$response = e.response) == null ? void 0 : _e$response.status) ?? (e == null ? void 0 : e.status);
    if (statusCode !== 400) {
      throw e;
    }
  }
  ConnectOrBindLogger.onConnectOrBindCreateStart == null || ConnectOrBindLogger.onConnectOrBindCreateStart({
    domainName: rootDomain,
    connectionType,
    msid
  });
  const {
    connectedDomain
  } = await createConnectedDomain({
    connectedByEntri: !!connectedByEntri,
    connectedDomain: {
      domain: rootDomain,
      connectionType: connectionType === 'pointing' ? ConnectionType.POINTING : ConnectionType.NAMESERVERS,
      siteInfo: {
        id: msid
      }
    }
  });
  if (!(connectedDomain != null && (_connectedDomain$site = connectedDomain.siteInfo) != null && _connectedDomain$site.id)) {
    await assignDomainToSite({
      httpClient,
      domainName: rootDomain,
      siteId: msid
    });
  }
  return {
    domainName: rootDomain,
    msid,
    prevSiteId: site == null ? void 0 : site.siteId
  };
}
async function connectOrBindSubdomain(_ref4) {
  let {
    httpClient,
    subdomain,
    rootDomain,
    msid,
    connectedByEntri
  } = _ref4;
  const {
    getConnectedDomain,
    userDomain
  } = (await import('../ApiClients/DomainsApiClient')).DomainsApiClient.queries({
    httpClient
  });
  ConnectOrBindLogger.onConnectOrBindSubDomainStart == null || ConnectOrBindLogger.onConnectOrBindSubDomainStart({
    domainName: rootDomain,
    msid
  });
  const {
    error
  } = await userDomain({
    domain: subdomain
  });
  const isSubdomainInAccount = !error;
  if (isSubdomainInAccount) {
    await assignDomainToSite({
      httpClient,
      domainName: subdomain,
      siteId: msid
    });
    return {
      domainName: subdomain,
      msid
    };
  }
  if (error === 'belongsToAnotherAccount') {
    return {
      error: DomainAvailabilityStatus.DOMAIN_IN_ANOTHER_WIX_ACCOUNT
    };
  }
  ConnectOrBindLogger.onConnectOrBindSubdomainCreateStart == null || ConnectOrBindLogger.onConnectOrBindSubdomainCreateStart({
    domainName: subdomain,
    msid
  });
  try {
    const {
      connectedDomain
    } = await getConnectedDomain({
      connectedDomainId: rootDomain
    });
    if ((connectedDomain == null ? void 0 : connectedDomain.connectionType) === ConnectionType.POINTING) {
      const connectionTypeError = 'rootDomainIsConnectedByPointing';
      return {
        error: connectionTypeError
      };
    }
    return await createAndBindSubdomain({
      httpClient,
      msid,
      subdomain,
      connectedByEntri
    });
  } catch (e) {
    var _e$response2;
    const statusCode = (e == null || (_e$response2 = e.response) == null ? void 0 : _e$response2.status) ?? (e == null ? void 0 : e.status);
    switch (statusCode) {
      case 404:
        {
          return await createAndBindSubdomain({
            httpClient,
            msid,
            subdomain,
            connectedByEntri
          });
        }
      default:
        {
          throw e;
        }
    }
  }
}
const createAndBindSubdomain = async _ref5 => {
  var _connectedDomainData$;
  let {
    httpClient,
    subdomain,
    msid,
    connectedByEntri
  } = _ref5;
  const {
    createConnectedDomain
  } = (await import('../ApiClients/DomainsApiClient')).DomainsApiClient.queries({
    httpClient
  });
  const {
    connectedDomain: connectedDomainData
  } = await createConnectedDomain({
    connectedByEntri: !!connectedByEntri,
    connectedDomain: {
      domain: subdomain,
      siteInfo: {
        id: msid
      }
    }
  });
  if (!(connectedDomainData != null && (_connectedDomainData$ = connectedDomainData.siteInfo) != null && _connectedDomainData$.id)) {
    await assignDomainToSite({
      httpClient,
      domainName: subdomain,
      siteId: msid
    });
  }
  return {
    domainName: subdomain,
    msid
  };
};
//# sourceMappingURL=connectOrBindDomain.js.map